import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBookmark,
  faEllipsisH,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import { faBookmark as faBookmarkRegular } from '@fortawesome/free-regular-svg-icons';
import { Button } from 'react-bootstrap';
import { UserInfo, VisualCache } from '../helper/Context';
import { loadChannelConfig } from '../helper/ChannelConfig';
import { generateMockEngagementForVisual } from '../helper/MockEngagementService';
import ConversationModeSimulator from '../components/ConversationModeSimulator';
import AnimatedTextDisplay from '../components/AnimatedTextDisplay';
import './MyOrbitChannel.css';

const ChannelPreview = () => {
  const { userInfo } = useContext(UserInfo);
  const { visCache } = useContext(VisualCache);
  const navigate = useNavigate();
  
  const [channelConfig, setChannelConfig] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userInfo || !userInfo.email) {
      navigate('/content-dashboard');
      return;
    }

    const config = loadChannelConfig(userInfo.email);
    if (!config || config.selectedVisuals.length === 0) {
      navigate('/content-dashboard');
      return;
    }

    setChannelConfig(config);

    const convertVisualsToPost = (visualIds) => {
      const visualPosts = [];
      
      visualIds.forEach((visualId, index) => {
        const visual = visCache[visualId];
        if (!visual) return;

        const engagement = generateMockEngagementForVisual(visual);
        
        const timeAgo = index === 0 ? '2 hours ago' : 
                       index === 1 ? '5 hours ago' :
                       index === 2 ? '1 day ago' :
                       index < 6 ? `${index - 1} days ago` :
                       `${Math.ceil(index / 7)} weeks ago`;

        let postType = 'iframe';
        let visualUrl = visual.visualization_url;

        if (visual.type === 'video' || visual.type === 'explainer' || visual.type === 'tour') {
          postType = 'local_video';
          visualUrl = visual.visualization_url || visual.thumbnail;
        } else if (visual.type === 'presentation' || visual.type === 'slideshow' || visual.type === 'scrollytelling') {
          postType = 'iframe';
          visualUrl = visual.visualization_url;
        } else if (visual.thumbnail && visual.thumbnail.endsWith('.mp4')) {
          postType = 'local_video';
          visualUrl = visual.thumbnail;
        } else {
          postType = 'iframe';
          visualUrl = visual.visualization_url;
        }

        visualPosts.push({
          id: visual.id,
          username: config.channelName,
          userAvatar: config.avatar,
          caption: visual.description || visual.title || 'Check out this visualization',
          visualUrl: visualUrl,
          type: postType,
          likes: engagement.likes,
          comments: engagement.comments,
          audio: visual.title || 'Original',
          tags: 0,
          isLiked: false,
          isSaved: false,
          timestamp: timeAgo,
          visualData: visual
        });
      });

      return visualPosts;
    };

    const generatedPosts = convertVisualsToPost(config.visualOrder);
    setPosts(generatedPosts);
    setLoading(false);
  }, [userInfo, visCache, navigate]);

  const handleLike = (postId) => {
    setPosts(posts.map(post => 
      post.id === postId 
        ? { ...post, isLiked: !post.isLiked, likes: post.isLiked ? post.likes - 1 : post.likes + 1 }
        : post
    ));
  };

  const handleSave = (postId) => {
    setPosts(posts.map(post => 
      post.id === postId 
        ? { ...post, isSaved: !post.isSaved }
        : post
    ));
  };

  const [currentPostIndex, setCurrentPostIndex] = useState(0);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (currentPostIndex < posts.length - 1) {
          const nextIndex = currentPostIndex + 1;
          const postElements = document.querySelectorAll('.orbit-post');
          if (postElements[nextIndex]) {
            postElements[nextIndex].scrollIntoView({ behavior: 'smooth', block: 'start' });
            setCurrentPostIndex(nextIndex);
          }
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (currentPostIndex > 0) {
          const prevIndex = currentPostIndex - 1;
          const postElements = document.querySelectorAll('.orbit-post');
          if (postElements[prevIndex]) {
            postElements[prevIndex].scrollIntoView({ behavior: 'smooth', block: 'start' });
            setCurrentPostIndex(prevIndex);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPostIndex, posts]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const postElements = Array.from(document.querySelectorAll('.orbit-post'));
            const index = postElements.indexOf(entry.target);
            if (index !== -1) {
              setCurrentPostIndex(index);
            }
          }
        });
      },
      {
        root: null,
        threshold: 0.5
      }
    );

    const postElements = document.querySelectorAll('.orbit-post');
    postElements.forEach((el) => observer.observe(el));

    return () => {
      postElements.forEach((el) => observer.unobserve(el));
    };
  }, [posts]);

  if (loading) {
    return (
      <div className="orbit-channel-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Loading preview...</div>
      </div>
    );
  }

  return (
    <div className="orbit-channel-container">
      {/* Header with back button */}
      <div className="orbit-channel-header">
        <div className="orbit-header-left">
          <Button 
            variant="link" 
            onClick={() => navigate('/content-dashboard')}
            style={{ 
              color: '#262626', 
              textDecoration: 'none',
              padding: '0 0.5rem',
              fontSize: '1.1rem'
            }}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </Button>
          <span className="orbit-logo">{channelConfig?.channelName || 'My Channel'}</span>
          <span className="orbit-logo-subtitle">Preview Mode</span>
        </div>
      </div>

      {/* Feed */}
      <div className="orbit-feed">
        {posts.map((post) => (
          <OrbitPost 
            key={post.id} 
            post={post} 
            onLike={handleLike}
            onSave={handleSave}
          />
        ))}
      </div>

      {/* Navigation Bar */}
      <div className="orbit-nav-bar">
        <div className="orbit-nav-item">
          <div className="orbit-nav-icon">🔍</div>
        </div>
        <div className="orbit-nav-item orbit-nav-active">
          <div className="orbit-nav-icon">🔔</div>
        </div>
        <div className="orbit-nav-item">
          <div className="orbit-nav-avatar">👤</div>
        </div>
      </div>
    </div>
  );
};

const OrbitPost = ({ post, onLike, onSave }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const postRef = useRef(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    const currentPostRef = postRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting);
          
          if (post.type === 'local_video' && iframeRef.current) {
            if (entry.isIntersecting) {
              iframeRef.current.play().catch(err => console.log('Play error:', err));
            } else {
              iframeRef.current.pause();
            }
          }
          
          if (post.type === 'youtube' && iframeRef.current) {
            setTimeout(() => {
              const command = entry.isIntersecting ? 'playVideo' : 'pauseVideo';
              iframeRef.current.contentWindow?.postMessage(
                JSON.stringify({ event: 'command', func: command, args: [] }),
                '*'
              );
            }, 500);
          }
        });
      },
      {
        root: null,
        threshold: 0.5
      }
    );

    if (currentPostRef) {
      observer.observe(currentPostRef);
    }

    return () => {
      if (currentPostRef) {
        observer.unobserve(currentPostRef);
      }
    };
  }, [post.type]);

  const handleVideoShow = useCallback((showing) => {
    setIsVideoPlaying(showing);
  }, []);

  return (
    <div ref={postRef} className="orbit-post">
      {/* Post Header */}
      <div className="orbit-post-header">
        <div className="orbit-post-user-info">
          <div className="orbit-post-avatar">{post.userAvatar}</div>
          <div className="orbit-post-user-details">
            <span className="orbit-post-username">{post.username}</span>
            <span className="orbit-post-timestamp">{post.timestamp}</span>
          </div>
        </div>
        <div className="orbit-post-follow-section">
          <button className="orbit-follow-button">Follow</button>
          <FontAwesomeIcon icon={faEllipsisH} className="orbit-post-menu" />
        </div>
      </div>

      {/* Visual Content */}
      <div className="orbit-post-visual">
        {post.type === 'conversation' ? (
          <ConversationModeSimulator 
            isActive={isVisible} 
            onVideoShow={handleVideoShow}
          />
        ) : post.type === 'animated_text' ? (
          <AnimatedTextDisplay 
            isActive={isVisible}
            onVideoShow={handleVideoShow}
          />
        ) : post.type === 'local_video' ? (
          <div className="orbit-video-container">
            <video
              ref={iframeRef}
              src={post.visualUrl}
              loop
              playsInline
              muted
              className="orbit-video-player"
            />
          </div>
        ) : (
          <iframe
            ref={iframeRef}
            src={post.visualUrl}
            title={post.caption}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              display: 'block',
              backgroundColor: '#000'
            }}
          />
        )}
      </div>

      {/* Action Buttons */}
      {!isVideoPlaying && (
        <div className="orbit-post-actions">
          <div className="orbit-post-actions-left">
            <button 
              className="orbit-action-button"
              onClick={() => onSave(post.id)}
            >
              <FontAwesomeIcon 
                icon={post.isSaved ? faBookmark : faBookmarkRegular}
                className={post.isSaved ? "orbit-saved" : ""}
              />
            </button>
          </div>
        </div>
      )}

      {/* Caption */}
      {!isVideoPlaying && (
        <div className="orbit-post-caption">
          <span className="orbit-caption-username">{post.username}</span>
          <span className="orbit-caption-text">{post.caption}</span>
        </div>
      )}
    </div>
  );
};

export default ChannelPreview;
